import * as React from 'react';
import moment from 'moment';
import 'moment-timezone';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  ThemeProvider,
  createTheme,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormValues, TextConfig, DateConfig, RadioConfig, FormComponentProps } from '@qfa/forms';
import useBlockStyles from './styles';
import { BlockMode, BlockPublicStreamPopoverProps } from './types';
import { KineticForm } from 'components/forms';
import { useDateTimeFormatPreference, useForm } from 'kineticUtils';
import { useLocalizer } from 'components/TaskScheduler';

function isValidDurationStrict(raw: string): boolean {
  if (!raw) {
    return false;
  }
  const trimmedDuration = raw.trim();
  // Validates a duration string like "2h30m15s"; must include at least one unit (h/m/s) and no zeros.
  const pattern = /^(?:(?:[1-9]\d*)h)?(?:(?:[1-9]\d*)m)?(?:(?:[1-9]\d*)s)?$/;
  return pattern.test(trimmedDuration) && /[hms]/.test(trimmedDuration);
}

const BlockPublicStreamPopover: React.FC<BlockPublicStreamPopoverProps> = ({
  open,
  containerEl,
  anchorEl,
  panelRef,
  onClose,
  onSubmit,
  cameraLabel,
  currentBlock,
  onUnblock,
}) => {
  const { t } = useTranslation('video');
  const outerTheme = useTheme();
  useLocalizer();

  const darkTheme = React.useMemo(() => {
    const darkPalette = createTheme({ palette: { mode: 'dark' } });
    return createTheme({ ...outerTheme, palette: darkPalette.palette });
  }, [outerTheme]);

  const classes = useBlockStyles();
  const { userDateTimeFormat } = useDateTimeFormatPreference();
  const isBlocked = Boolean(currentBlock);

  const [pos, setPos] = React.useState<{ left: number; top: number }>({ left: 0, top: 0 });

  const computePosition = React.useCallback(() => {
    if (!containerEl || !anchorEl) {
      return;
    }
    const containerRect = containerEl.getBoundingClientRect();
    const anchorRect = anchorEl.getBoundingClientRect();
    setPos({
      left: anchorRect.left + anchorRect.width / 2 - containerRect.left,
      top: anchorRect.top - containerRect.top,
    });
  }, [containerEl, anchorEl]);

  React.useLayoutEffect(() => {
    computePosition();
  }, [computePosition, open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const handleScroll = () => computePosition();
    const handleResize = () => computePosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, computePosition]);

  type BlockFormValues = FormValues & {
    mode: BlockMode;
    duration?: string;
    until?: moment.Moment | null;
  };

  const {
    props: formProps,
    values: [values, setValues],
  } = useForm<BlockFormValues>({
    fields: [
      RadioConfig({
        id: 'mode',
        name: 'mode',
        label: '',
        options: [
          { label: t('duration'), value: 'duration' },
          { label: t('until'), value: 'until' },
          { label: t('noEndTime'), value: 'indefinite' },
        ],
      }),
      TextConfig({
        id: 'duration',
        name: 'duration',
        label: t('durationLabel'),
        placeholder: t('durationPlaceholder'),
      }),
      DateConfig({
        id: 'until',
        name: 'until',
        label: t('endTimeLabel'),
        disablePast: true,
        displayFormat: userDateTimeFormat,
      }),
    ],
    defaultValues: {
      mode: 'duration',
      duration: '15m',
      until: moment().add(1, 'hour'),
    },
  });

  const mode = (values.mode as BlockMode) ?? 'duration';
  const durationString = (values.duration ?? '').toString();
  const applyDisabled =
    (mode === 'duration' && durationString.trim().length === 0) ||
    !isValidDurationStrict(durationString);

  const unblockStream = () => {
    onUnblock();
    onClose();
    return;
  };

  const handleApply = () => {
    if (mode === 'indefinite') {
      onSubmit({ mode: 'indefinite' });
      onClose();
      return;
    }
    if (mode === 'duration') {
      if (!isValidDurationStrict(durationString)) {
        return;
      }
      onSubmit({ mode: 'duration', duration: durationString.trim() });
      onClose();
      return;
    }
    const untilMoment = moment(values.until);
    if (!untilMoment.isValid() || untilMoment.isSameOrBefore(moment())) {
      console.error(t('invalidEndTime'), { until: values.until });
      return;
    }
    onSubmit({ mode: 'until', until: untilMoment.toISOString() });
    onClose();
  };

  const unblockIndicator = React.useMemo(() => {
    if (!isBlocked || !currentBlock) {
      return null;
    }

    const candidateIso =
      (currentBlock.blockedUntil && currentBlock.blockedUntil.trim()) ||
      (currentBlock.endsAt && currentBlock.endsAt.trim()) ||
      null;

    if (!candidateIso) {
      return (
        <Typography variant='body2' sx={{ mt: 0.5, color: 'warning.main' }}>
          {t('blockedIndefinitely')}
        </Typography>
      );
    }

    const endsAt = moment(candidateIso);
    if (!endsAt.isValid() || endsAt.isBefore(moment())) {
      return null;
    }

    const atText = endsAt.format(userDateTimeFormat);
    return (
      <Typography variant='body2' sx={{ mt: 0.5, color: 'warning.main' }}>
        {t('unblocksAt')} <strong>{atText}</strong>
      </Typography>
    );
  }, [isBlocked, currentBlock, userDateTimeFormat, t]);

  if (!open || !containerEl || !anchorEl) {
    return null;
  }

  type KineticFormRenderProps = { fieldComponents: FormComponentProps<BlockFormValues> };
  return (
    <ThemeProvider theme={darkTheme}>
      <div
        ref={panelRef}
        className={classes.panel}
        style={{ left: pos.left, top: pos.top }}
        onWheel={(wheelEvent) => wheelEvent.stopPropagation()}
      >
        <Box className={classes.paper}>
          <Typography variant='subtitle1' className={classes.title} gutterBottom>
            {t('blockPublicStreamTitle')}
            {cameraLabel ? ` · ${cameraLabel}` : ''}
          </Typography>

          {unblockIndicator}

          <KineticForm {...formProps}>
            {({ fieldComponents }: KineticFormRenderProps) => (
              <>
                <div className={classes.modeGroup}>{fieldComponents.mode.default}</div>

                {mode === 'duration' && (
                  <Stack spacing={1} sx={{ mt: 1, width: '100%' }}>
                    <div>{fieldComponents.duration.default}</div>
                    {applyDisabled && (
                      <Typography variant='caption' color='error' role='alert' sx={{ mt: 0.5 }}>
                        {t('invalidDuration')}
                      </Typography>
                    )}
                    <div className={classes.presetsRow}>
                      {['15m', '30m', '1h', '4h', '8h', '24h'].map((preset) => (
                        <Chip
                          key={preset}
                          label={preset}
                          variant='outlined'
                          size='small'
                          onClick={() => setValues({ ...values, duration: preset })}
                        />
                      ))}
                    </div>
                  </Stack>
                )}

                {mode === 'until' && <Box sx={{ mt: 1 }}>{fieldComponents.until.default}</Box>}

                <Box
                  className={classes.actions}
                  sx={{ width: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {isBlocked && (
                    <Button
                      onClick={unblockStream}
                      variant='outlined'
                      color='error'
                      aria-label={t('unblock')}
                    >
                      {t('unblock')}
                    </Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button onClick={onClose}>{t('cancel')}</Button>
                  <Button variant='contained' onClick={handleApply} disabled={applyDisabled}>
                    {isBlocked ? t('update') : t('block')}
                  </Button>
                </Box>
              </>
            )}
          </KineticForm>
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default BlockPublicStreamPopover;
